// Resolves the signed-in user's plan state for marketing-page consumers
// (Product Hunt landing + Pricing page). Returns a single discriminated
// union so the floating account pill + welcome popup can render the right
// variant without each repeating the same DB calls.
//
// Mounted on public marketing surfaces — outside AuthProvider — so it
// talks to supabase directly. Cheap: 1 auth call + at most 2 table reads
// (agencies by owner_id, agency_subscriptions by agency_id), cached for
// 60 s via React Query so repeated mounts across the page don't hammer
// the DB.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthSession } from '@/hooks/useAuthSession';

export type PlanState =
  | { state: 'unauthenticated' }
  | { state: 'loading' }
  | { state: 'no_agency'; user: AuthedUser }
  | { state: 'trial_active'; user: AuthedUser; agency: Agency; sub: Sub; daysLeft: number; creditsLeft: number; creditsTotal: number }
  | { state: 'trial_expired'; user: AuthedUser; agency: Agency; sub: Sub }
  | { state: 'ph_pending'; user: AuthedUser; agency: Agency; sub: Sub }
  | { state: 'active'; user: AuthedUser; agency: Agency; sub: Sub; creditsLeft: number; creditsTotal: number; renewsAt: string | null }
  | { state: 'lifetime'; user: AuthedUser; agency: Agency; sub: Sub; creditsLeft: number; creditsTotal: number }
  | { state: 'cancelled'; user: AuthedUser; agency: Agency; sub: Sub };

interface AuthedUser {
  id: string;
  email: string | null;
  displayName: string;
  avatarUrl: string | null;
}

interface Agency {
  id: string;
  name: string | null;
  slug: string | null;
}

interface Sub {
  id: string;
  plan_slug: string;
  billing_cycle: string;
  status: string;
  charge_currency: string;
  credits_per_period: number;
  credits_used_this_period: number;
  current_period_end: string | null;
  cancelled_at: string | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  is_lifetime: boolean;
  offer: string | null;
  coupon_id: string | null;
  razorpay_subscription_id: string | null;
}

function daysBetween(now: Date, future: string | null): number {
  if (!future) return 0;
  const diff = new Date(future).getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

async function fetchAgencyState(userId: string): Promise<{
  user: { id: string };
  agency: Agency | null;
  sub: Sub | null;
}> {
  // Find the agency the user owns. A user can own at most one agency in
  // the lead-gen funnel — if multi-agency support arrives later, swap
  // this for the active-workspace pointer.
  const { data: agencies, error: agencyErr } = await supabase
    .from('agencies')
    .select('id, name, slug')
    .eq('owner_id', userId)
    .limit(1);

  if (agencyErr) {
    throw new Error(`agencies lookup failed: ${agencyErr.message}`);
  }

  const agency = agencies?.[0] ?? null;
  if (!agency) {
    return { user: { id: userId }, agency: null, sub: null };
  }

  const { data: subs, error: subErr } = await supabase
    .from('agency_subscriptions')
    .select(
      'id, plan_slug, billing_cycle, status, charge_currency, credits_per_period, credits_used_this_period, current_period_end, cancelled_at, trial_started_at, trial_ends_at, is_lifetime, offer, coupon_id, razorpay_subscription_id',
    )
    .eq('agency_id', agency.id)
    .order('created_at', { ascending: false })
    .limit(1);

  if (subErr) {
    throw new Error(`agency_subscriptions lookup failed: ${subErr.message}`);
  }

  return {
    user: { id: userId },
    agency,
    sub: (subs?.[0] as Sub | undefined) ?? null,
  };
}

export function useUserPlanState(): PlanState {
  const { user, loading: authLoading } = useAuthSession();

  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['userPlanState', user?.id ?? null],
    queryFn: () => fetchAgencyState(user!.id),
    enabled: !!user?.id,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  if (authLoading) return { state: 'loading' };
  if (!user) return { state: 'unauthenticated' };
  if (queryLoading && !data) return { state: 'loading' };

  const meta = (user.user_metadata ?? {}) as { name?: string; full_name?: string; avatar_url?: string; picture?: string };
  const authed: AuthedUser = {
    id: user.id,
    email: user.email ?? null,
    displayName: meta.full_name || meta.name || user.email || 'You',
    avatarUrl: meta.avatar_url || meta.picture || null,
  };

  if (!data?.agency) {
    return { state: 'no_agency', user: authed };
  }
  const { agency, sub } = data;

  if (!sub) {
    return { state: 'no_agency', user: authed };
  }

  // Lifetime takes precedence over everything else.
  if (sub.is_lifetime) {
    return {
      state: 'lifetime',
      user: authed,
      agency,
      sub,
      creditsLeft: Math.max(0, sub.credits_per_period - sub.credits_used_this_period),
      creditsTotal: sub.credits_per_period,
    };
  }

  const now = new Date();

  if (sub.status === 'trialing') {
    return {
      state: 'trial_active',
      user: authed,
      agency,
      sub,
      daysLeft: daysBetween(now, sub.trial_ends_at),
      creditsLeft: Math.max(0, sub.credits_per_period - sub.credits_used_this_period),
      creditsTotal: sub.credits_per_period,
    };
  }

  if (sub.status === 'trial_expired') {
    return { state: 'trial_expired', user: authed, agency, sub };
  }

  if (sub.status === 'cancelled' || sub.cancelled_at) {
    return { state: 'cancelled', user: authed, agency, sub };
  }

  // PH offer claimed but no successful Razorpay charge yet (sub created
  // but mandate never authorized — the Manu case we just debugged).
  if (sub.offer === 'producthunt' && !sub.razorpay_subscription_id && sub.plan_slug === 'free') {
    return { state: 'ph_pending', user: authed, agency, sub };
  }

  // Anything else with a paid plan_slug → active subscriber.
  if (['starter', 'growth', 'scale'].includes(sub.plan_slug) && sub.status === 'active') {
    return {
      state: 'active',
      user: authed,
      agency,
      sub,
      creditsLeft: Math.max(0, sub.credits_per_period - sub.credits_used_this_period),
      creditsTotal: sub.credits_per_period,
      renewsAt: sub.current_period_end,
    };
  }

  // Fallback: treat as no-agency-style for safety. Surfaces the trial CTA
  // instead of pretending we know their state.
  return { state: 'no_agency', user: authed };
}
